/**
 * <s-policy-sections> — splits a store policy body into numbered cards.
 *
 * A new section starts at each heading: h1–h4, or a paragraph whose only content is
 * bold text (how many policy editors write headings). Content before the first
 * heading, or under a leading heading that names the policy, becomes an unnumbered
 * intro card. Without JavaScript the policy shows as one card.
 *
 * Attributes: data-overline — small label shown above each section title.
 */
class SPolicySections extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    const source = this.querySelector('[data-policy-source]');
    if (!source) return;

    const nodes = [...source.childNodes].filter((n) => n.nodeType === 1 || (n.nodeType === 3 && n.textContent.trim()));
    const groups = [];
    let current = { title: null, items: [] };
    nodes.forEach((node, index) => {
      if (this.#isHeading(node)) {
        if (current.title || current.items.length) groups.push(current);
        current = { title: node.textContent.trim(), items: [], first: index === 0 };
      } else {
        current.items.push(node);
      }
    });
    if (current.title || current.items.length) groups.push(current);
    if (groups.length < 2) return;

    const list = document.createElement('div');
    list.className = 'pp-body';
    let number = 0;
    groups.forEach((group) => {
      const intro = !group.title || (group.first && /policy|terms|notice|information|conditions/i.test(group.title));
      const card = document.createElement('section');
      card.className = 'pp-card';
      if (!intro) {
        number += 1;
        const num = document.createElement('span');
        num.className = 'pp-num';
        num.setAttribute('aria-hidden', 'true');
        num.textContent = String(number).padStart(2, '0');
        card.append(num);
      }
      const main = document.createElement('div');
      main.className = 'pp-main';
      if (group.title) {
        if (!intro && this.dataset.overline) {
          const over = document.createElement('span');
          over.className = 'pp-over';
          over.textContent = this.dataset.overline;
          main.append(over);
        }
        const heading = document.createElement('h2');
        heading.className = 'pp-q';
        heading.textContent = group.title;
        main.append(heading);
      }
      const body = document.createElement('div');
      body.className = 'pp-a';
      group.items.forEach((item) => body.append(item));
      main.append(body);
      card.append(main);
      list.append(card);
    });

    (source.closest('[data-policy-fallback]') || source).replaceWith(list);
    this.dataset.ready = 'true';
  }

  #isHeading(node) {
    if (node.nodeType !== 1) return false;
    // data-heading-tags (e.g. "H2") limits sections to those tags; otherwise any h1–h4 or a bold-only paragraph.
    const only = this.dataset.headingTags?.split(',').map((t) => t.trim().toUpperCase());
    if (only) return only.includes(node.tagName);
    if (/^H[1-4]$/.test(node.tagName)) return true;
    if (node.tagName !== 'P') return false;
    const kids = [...node.childNodes].filter((n) => n.nodeType === 1 || n.textContent.trim());
    return kids.length === 1 && kids[0].nodeType === 1 && /^(STRONG|B)$/.test(kids[0].tagName) && node.textContent.trim().length < 90;
  }
}

if (!customElements.get('s-policy-sections')) customElements.define('s-policy-sections', SPolicySections);
